/**
 * Trip Request Form Page Component
 * /admin/trip-requests/new and /admin/trip-requests/:id/edit
 *
 * Captures what the customer is asking for: who travels, which movements are
 * needed and when, what will be carried, and which corporate references apply.
 * It deliberately offers no vehicle or driver control - those are chosen at the
 * assignment stage, after a request is accepted.
 *
 * One component serves Direct and Corporate requests. The profile changes which
 * sections appear, not which form is used, so the two cannot drift apart. Every
 * rule mirrored here is enforced again on the server, which is authoritative.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
  AlertTitle,
  FormControl,
  InputLabel,
  Select,
  Divider,
  IconButton,
  Chip,
  Checkbox,
  FormControlLabel,
  Tooltip,
  Stepper,
  Step,
  StepButton,
  StepLabel,
  Card,
  CardContent,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import apiClient from '../api/client';

// Which step each validation key belongs to, so a failure lights up the step
// that owns it. A stepper hides most of the form at any moment; without this
// an error on a step you are not looking at is invisible and the submit
// button simply refuses to work with no explanation.
const ERROR_STEP = {
  customer_name: 'customer',
  round_trip_mode: 'type',
  expected_waiting_hours: 'type',
  legs: 'journey',
  declared_traveller_count: 'travellers',
  travellers: 'travellers',
  luggage: 'travellers',
  minimum_seats: 'vehicle',
  capacity: 'vehicle',
};

// localStorage key for the in-progress draft. Scoped by request id so a new
// request and an edit of an existing one cannot overwrite each other.
const draftKey = (id) => `fleetcare:trip-request-draft:${id || 'new'}`;

// The steps, in order. `commercial` is dropped for a direct request: there is
// no corporate allocation to capture and the server rejects those fields, so
// showing an empty step would only invite the user to fill in something that
// cannot be saved.
const STEP_DEFINITIONS = [
  { key: 'type', label: 'Request Type', caption: 'Who is asking, and what shape of journey' },
  { key: 'customer', label: 'Customer & Requester', caption: 'Account and contacts' },
  { key: 'journey', label: 'Journey', caption: 'Legs, timings and flights' },
  { key: 'travellers', label: 'Travellers & Luggage', caption: 'Who travels and what they carry' },
  { key: 'vehicle', label: 'Vehicle Requirements', caption: 'Seats, category and assistance' },
  { key: 'commercial', label: 'Commercial & Billing', caption: 'Allocation and authorisation' },
  { key: 'review', label: 'Review & Submit', caption: 'Confirm and record the request' },
];

const JOURNEY_TYPES = [
  { value: 'one_way', label: 'One Way' },
  { value: 'round_trip', label: 'Round Trip' },
];

const ROUND_TRIP_MODES = [
  { value: 'continuous_with_wait', label: 'Continuous — vehicle waits' },
  { value: 'split_date_return', label: 'Split date — return on another day' },
];

const LEG_SERVICE_TYPES = [
  { value: 'point_to_point', label: 'Point to Point' },
  { value: 'airport_dropoff', label: 'Airport Drop-off' },
  { value: 'airport_pickup', label: 'Airport Pickup' },
  { value: 'accommodation_relocation', label: 'Accommodation Relocation' },
  { value: 'office_relocation', label: 'Office Relocation' },
  { value: 'other', label: 'Other' },
];

const AIRPORT_SERVICE_TYPES = ['airport_dropoff', 'airport_pickup'];

const TRAVELLER_CATEGORIES = [
  { value: 'adult', label: 'Adult' },
  { value: 'child', label: 'Child' },
  { value: 'infant', label: 'Infant' },
];

const TRAVELLER_RELATIONSHIPS = [
  { value: 'customer', label: 'Customer' },
  { value: 'family', label: 'Family' },
  { value: 'guest', label: 'Guest' },
  { value: 'employee', label: 'Corporate Employee' },
  { value: 'corporate_guest', label: 'Corporate Guest' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'visitor', label: 'Visitor' },
  { value: 'other', label: 'Other' },
];

const LOAD_CATEGORIES = [
  { value: 'cabin_bag', label: 'Cabin Bag' },
  { value: 'checked_suitcase', label: 'Checked Suitcase' },
  { value: 'large_suitcase', label: 'Large Suitcase' },
  { value: 'box_carton', label: 'Box / Carton' },
  { value: 'stroller', label: 'Stroller' },
  { value: 'wheelchair', label: 'Wheelchair' },
  { value: 'sports_equipment', label: 'Sports Equipment' },
  { value: 'tools_equipment', label: 'Tools / Equipment' },
  { value: 'oversized_item', label: 'Oversized or Special Item' },
  { value: 'other', label: 'Other' },
];

const ATTACHMENT_TYPES = [
  { value: 'air_ticket', label: 'Air Ticket' },
  { value: 'travel_itinerary', label: 'Travel Itinerary' },
  { value: 'customer_request', label: 'Customer Request' },
  { value: 'passenger_list', label: 'Passenger List' },
  { value: 'luggage_list', label: 'Luggage / Load List' },
  { value: 'other', label: 'Other Supporting Document' },
];

// Labels for the corporate fields the server may require. Only keys the policy
// response returns as visible are rendered, so this list is presentation only.
const CORPORATE_FIELD_LABELS = {
  department: 'Department',
  cost_center_code: 'Cost Centre',
  project_code: 'Project Code',
  job_number: 'Job Number',
  work_order_number: 'Work Order',
  internal_order: 'Internal Order',
  service_order: 'Service Order',
  business_unit: 'Business Unit',
  division: 'Division',
  gl_account_code: 'GL / Account Code',
  site_location: 'Site / Location',
  purpose_of_travel: 'Purpose of Travel',
  charge_allocation_description: 'Charge Allocation',
  corporate_request_number: 'Corporate Request Number',
  customer_booking_reference: 'Customer Booking Reference',
  po_lpo_number: 'PO / LPO Number',
  contract_reference: 'Contract Reference',
  authorization_number: 'Authorisation Number',
  approval_reference: 'Approval Reference',
  corporate_approver_name: 'Approver',
  travel_desk_reference: 'Travel Desk Reference',
  billing_entity: 'Billing Entity',
  billing_branch: 'Billing Branch',
  billing_instructions: 'Billing Instructions',
  invoice_narration: 'Invoice Narration',
};

let keySeed = 0;
const nextKey = () => {
  keySeed += 1;
  return `k${keySeed}`;
};

// A restored draft carries the `_key` values it was saved with, but the seed
// resets on every page load. Without advancing past them the next generated
// key repeats one already in the draft, and two siblings end up sharing a
// React key - which React resolves by reusing the wrong row.
const reserveKeys = (items) => {
  (items || []).forEach((item) => {
    const parsed = Number.parseInt(String(item?._key || '').slice(1), 10);
    if (Number.isInteger(parsed)) keySeed = Math.max(keySeed, parsed);
    if (item?.load_items) reserveKeys(item.load_items);
  });
};

const toLocalInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

const toIso = (value) => (value ? new Date(value).toISOString() : null);

const emptyLeg = (role, sequence) => ({
  _key: nextKey(),
  id: null,
  sequence,
  leg_role: role,
  service_type: 'point_to_point',
  requested_pickup_datetime: '',
  pickup_location: '',
  dropoff_location: '',
  expected_passenger_count: 1,
  accessibility_required: false,
  child_seat_required: false,
  special_instructions: '',
  airline: '',
  flight_number: '',
  departure_airport: '',
  arrival_airport: '',
  terminal: '',
  scheduled_flight_departure: '',
  scheduled_flight_arrival: '',
  ticket_baggage_allowance_kg: '',
  flight_notes: '',
  load_items: [],
  traveller_ids: [],
});

const emptyTraveller = (sequence) => ({
  _key: nextKey(),
  id: null,
  sequence,
  full_name: '',
  is_primary: sequence === 1,
  traveller_category: 'adult',
  traveller_relationship: 'customer',
  contact_number: '',
  employee_reference: '',
  assistance_requirements: '',
});

const TripRequestForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [customers, setCustomers] = useState([]);
  const [warnings, setWarnings] = useState([]);

  const [requestProfile, setRequestProfile] = useState('direct');
  const [fieldRequirements, setFieldRequirements] = useState({});

  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_reference: '',
    requester_name: '',
    requester_phone: '',
    requester_email: '',
    primary_contact_name: '',
    primary_contact_phone: '',
    journey_type: 'one_way',
    round_trip_mode: '',
    expected_waiting_hours: '',
    declared_traveller_count: 1,
    minimum_seats: '',
    accessibility_required: false,
    child_seat_required: false,
    priority: 'normal',
    preferred_vehicle_type: '',
    notes: '',
    internal_notes: '',
  });
  const [corporateData, setCorporateData] = useState({});
  const [travellers, setTravellers] = useState([emptyTraveller(1)]);
  const [legs, setLegs] = useState([emptyLeg('outbound', 1)]);
  const [attachments, setAttachments] = useState([]);

  const [activeStep, setActiveStep] = useState(0);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);
  // A step is marked here once the user has left it. Flagging errors on a step
  // nobody has opened yet would paint a brand-new form red before a single
  // field has been touched.
  const [visitedSteps, setVisitedSteps] = useState(() => new Set());
  const [submitAttempted, setSubmitAttempted] = useState(false);
  // "Corporate" can be chosen before a customer has been picked, and until one
  // is the server still reports the request as direct. This records the intent
  // so the customer picker can be shown; it never overrides the server.
  const [profileChoice, setProfileChoice] = useState(null);
  // Set once the form has been populated, so the autosave effect does not
  // write an empty draft over a real one during the first render.
  const hydrated = useRef(false);

  const isCorporate = requestProfile === 'corporate';
  const steps = useMemo(
    () => STEP_DEFINITIONS.filter((step) => step.key !== 'commercial' || isCorporate),
    [isCorporate]
  );

  // ---- loading -------------------------------------------------------

  const loadFieldRequirements = useCallback(async (customerId) => {
    try {
      const data = await apiClient.getTripRequestFieldRequirements(customerId || null);
      setRequestProfile(data.request_profile || 'direct');
      setFieldRequirements(data.field_requirements || {});
    } catch (err) {
      // Policy is advisory here; the server enforces it regardless. Falling
      // back to "show nothing corporate" is the safe direction.
      setRequestProfile(customerId ? 'corporate' : 'direct');
      setFieldRequirements({});
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    try {
      const response = await apiClient.getCustomers({ is_active: true });
      setCustomers(Array.isArray(response) ? response : response.results || response.data || []);
    } catch (err) {
      setCustomers([]);
    }
  }, []);

  const loadTripRequest = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getTripRequest(id);

      if (data.booking) {
        setError(
          'This trip request has been converted to a booking and cannot be edited. ' +
            'Please edit the booking instead.'
        );
        setLoading(false);
        return;
      }
      if (data.status === 'completed' || data.status === 'cancelled') {
        setError(
          `This trip request has status "${data.status}" and cannot be edited. ` +
            'Only pending, assigned, or confirmed trip requests can be edited.'
        );
        setLoading(false);
        return;
      }

      setFormData({
        customer_id: data.customer_id || '',
        customer_name: data.customer_name || '',
        customer_email: data.customer_email || '',
        customer_phone: data.customer_phone || '',
        customer_reference: data.customer_reference || '',
        requester_name: data.requester_name || '',
        requester_phone: data.requester_phone || '',
        requester_email: data.requester_email || '',
        primary_contact_name: data.primary_contact_name || '',
        primary_contact_phone: data.primary_contact_phone || '',
        journey_type: data.journey_type || 'one_way',
        round_trip_mode: data.round_trip_mode || '',
        expected_waiting_hours:
          data.expected_waiting_hours === null || data.expected_waiting_hours === undefined
            ? ''
            : data.expected_waiting_hours,
        declared_traveller_count: data.declared_traveller_count || 1,
        minimum_seats: data.minimum_seats || '',
        accessibility_required: !!data.accessibility_required,
        child_seat_required: !!data.child_seat_required,
        priority: data.priority || 'normal',
        preferred_vehicle_type: data.preferred_vehicle_type || '',
        notes: data.notes || '',
        internal_notes: data.internal_notes || '',
      });

      const corporate = {};
      Object.keys(CORPORATE_FIELD_LABELS).forEach((key) => {
        corporate[key] = data[key] || '';
      });
      setCorporateData(corporate);

      setTravellers(
        (data.travellers || [])
          .filter((t) => t.is_active !== false)
          .map((t) => ({ ...t, _key: nextKey() }))
      );
      const activeLegs = (data.legs || [])
        .filter((l) => l.is_active !== false)
        .map((l) => ({
          ...l,
          _key: nextKey(),
          requested_pickup_datetime: toLocalInput(l.requested_pickup_datetime),
          scheduled_flight_departure: toLocalInput(l.scheduled_flight_departure),
          scheduled_flight_arrival: toLocalInput(l.scheduled_flight_arrival),
          ticket_baggage_allowance_kg: l.ticket_baggage_allowance_kg || '',
          load_items: (l.load_items || []).map((item) => ({ ...item, _key: nextKey() })),
          traveller_ids: l.participating_traveller_ids || [],
        }));
      if (activeLegs.length === 0) {
        // A pre-journey-capture request carries its itinerary on the header
        // and no leg rows. The server requires an outbound leg on every
        // write, so seed one restated from the header - the same
        // restatement migration 0012 performs - instead of leaving the
        // form leg-less, where "Add leg" would create an 'additional' leg
        // and every update would be refused.
        const seeded = emptyLeg('outbound', 1);
        seeded.pickup_location = data.pickup_location || '';
        seeded.dropoff_location = data.dropoff_location || '';
        seeded.requested_pickup_datetime = toLocalInput(data.scheduled_datetime);
        seeded.expected_passenger_count = data.declared_traveller_count || 1;
        activeLegs.push(seeded);
      }
      setLegs(activeLegs);
      setAttachments(
        (data.attachments || [])
          .filter((a) => a.is_active !== false)
          .map((a) => ({ ...a, _key: nextKey() }))
      );
      setWarnings(data.warnings || []);
      setRequestProfile(data.request_profile || data.customer_type || 'direct');
      setFieldRequirements(data.field_requirements || {});
    } catch (err) {
      setError(err.message || 'Failed to load trip request');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCustomers();
    if (isEdit) {
      loadTripRequest();
    } else {
      loadFieldRequirements(null);
    }
  }, [id, isEdit, loadCustomers, loadTripRequest, loadFieldRequirements]);

  // ---- header handlers ----------------------------------------------

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCorporateChange = (event) => {
    const { name, value } = event.target;
    setCorporateData((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Changing the customer re-resolves the profile and its requirements.
   *
   * Switching a corporate request to a direct one would strand any corporate
   * values already typed, so the user is asked before that data is dropped
   * rather than discovering later that it was silently kept or lost.
   */
  const handleCustomerChange = async (event) => {
    const nextCustomerId = event.target.value;
    const enteredCorporate = Object.entries(corporateData).filter(
      ([, value]) => value !== '' && value !== null && value !== undefined
    );

    if (!nextCustomerId && enteredCorporate.length > 0) {
      const labels = enteredCorporate
        .map(([key]) => CORPORATE_FIELD_LABELS[key] || key)
        .join(', ');
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        `Switching to a direct customer will discard the corporate details you have entered ` +
          `(${labels}). Continue?`
      );
      if (!confirmed) return false;
      setCorporateData({});
    }

    setFormData((prev) => ({ ...prev, customer_id: nextCustomerId }));
    await loadFieldRequirements(nextCustomerId || null);
    return true;
  };

  const handleJourneyTypeChange = (event) => {
    const journeyType = event.target.value;
    setFormData((prev) => ({
      ...prev,
      journey_type: journeyType,
      round_trip_mode: journeyType === 'round_trip' ? prev.round_trip_mode : '',
      expected_waiting_hours: journeyType === 'round_trip' ? prev.expected_waiting_hours : '',
    }));

    if (journeyType === 'round_trip') {
      setLegs((prev) => {
        if (prev.some((leg) => leg.leg_role === 'return')) return prev;
        const outbound = prev.find((leg) => leg.leg_role === 'outbound');
        const returnLeg = emptyLeg('return', prev.length + 1);
        if (outbound) {
          // Most returns retrace the outbound, so prefill and let the user
          // change it. Both remain fully editable.
          returnLeg.pickup_location = outbound.dropoff_location;
          returnLeg.dropoff_location = outbound.pickup_location;
          returnLeg.expected_passenger_count = outbound.expected_passenger_count;
        }
        return [...prev, returnLeg];
      });
    } else {
      const hasReturn = legs.some((leg) => leg.leg_role === 'return');
      if (hasReturn) {
        // eslint-disable-next-line no-alert
        const confirmed = window.confirm(
          'Switching to one way will remove the return leg and its luggage and flight ' +
            'details from this request. Continue?'
        );
        if (!confirmed) {
          setFormData((prev) => ({ ...prev, journey_type: 'round_trip' }));
          return;
        }
        setLegs((prev) => prev.filter((leg) => leg.leg_role !== 'return'));
      }
    }
  };

  const handleModeChange = (event) => {
    const mode = event.target.value;
    setFormData((prev) => ({
      ...prev,
      round_trip_mode: mode,
      // No vehicle stands by across a split-date return, so waiting hours are
      // not merely hidden - they are cleared, matching what the server stores.
      expected_waiting_hours: mode === 'split_date_return' ? '' : prev.expected_waiting_hours,
    }));
  };

  // ---- traveller handlers -------------------------------------------

  const addTraveller = () => {
    setTravellers((prev) => [...prev, emptyTraveller(prev.length + 1)]);
    setFormData((prev) => ({
      ...prev,
      declared_traveller_count: Math.max(prev.declared_traveller_count, travellers.length + 1),
    }));
  };

  const updateTraveller = (key, field, value) => {
    setTravellers((prev) =>
      prev.map((t) => {
        if (t._key !== key) {
          // Exactly one primary traveller: selecting a new one clears the rest.
          return field === 'is_primary' && value ? { ...t, is_primary: false } : t;
        }
        return { ...t, [field]: value };
      })
    );
  };

  const removeTraveller = (key) => {
    setTravellers((prev) =>
      prev.filter((t) => t._key !== key).map((t, index) => ({ ...t, sequence: index + 1 }))
    );
  };

  // ---- leg handlers --------------------------------------------------

  const updateLeg = (key, field, value) => {
    setLegs((prev) => prev.map((leg) => (leg._key === key ? { ...leg, [field]: value } : leg)));
  };

  const addLeg = () => {
    // The server refuses any request without an outbound leg, so the first
    // leg added to an empty itinerary must be the outbound one.
    setLegs((prev) => [
      ...prev,
      emptyLeg(
        prev.some((leg) => leg.leg_role === 'outbound') ? 'additional' : 'outbound',
        prev.length + 1
      ),
    ]);
  };

  const removeLeg = (key) => {
    setLegs((prev) =>
      prev.filter((leg) => leg._key !== key).map((leg, index) => ({ ...leg, sequence: index + 1 }))
    );
  };

  const applyAllTravellersToLeg = (key) => {
    const ids = travellers.filter((t) => t.id).map((t) => t.id);
    updateLeg(key, 'traveller_ids', ids);
  };

  const toggleLegTraveller = (legKey, travellerId) => {
    setLegs((prev) =>
      prev.map((leg) => {
        if (leg._key !== legKey) return leg;
        const current = leg.traveller_ids || [];
        return {
          ...leg,
          traveller_ids: current.includes(travellerId)
            ? current.filter((tid) => tid !== travellerId)
            : [...current, travellerId],
        };
      })
    );
  };

  // ---- load-item handlers -------------------------------------------

  const addLoadItem = (legKey) => {
    setLegs((prev) =>
      prev.map((leg) =>
        leg._key === legKey
          ? {
              ...leg,
              load_items: [
                ...leg.load_items,
                { _key: nextKey(), id: null, item_category: 'checked_suitcase', quantity: 1,
                  estimated_weight_kg: '', handling_requirements: '' },
              ],
            }
          : leg
      )
    );
  };

  const updateLoadItem = (legKey, itemKey, field, value) => {
    setLegs((prev) =>
      prev.map((leg) =>
        leg._key === legKey
          ? {
              ...leg,
              load_items: leg.load_items.map((item) =>
                item._key === itemKey ? { ...item, [field]: value } : item
              ),
            }
          : leg
      )
    );
  };

  const removeLoadItem = (legKey, itemKey) => {
    setLegs((prev) =>
      prev.map((leg) =>
        leg._key === legKey
          ? { ...leg, load_items: leg.load_items.filter((item) => item._key !== itemKey) }
          : leg
      )
    );
  };

  /**
   * Copy the outbound load onto the return.
   *
   * The copies are new rows without identifiers, so editing the return load
   * afterwards never rewrites the outbound one.
   */
  const copyOutboundLuggage = (targetKey) => {
    const outbound = legs.find((leg) => leg.leg_role === 'outbound');
    if (!outbound) return;
    setLegs((prev) =>
      prev.map((leg) =>
        leg._key === targetKey
          ? {
              ...leg,
              load_items: outbound.load_items.map((item) => ({
                ...item,
                _key: nextKey(),
                id: null,
              })),
            }
          : leg
      )
    );
  };

  // ---- attachment handlers -------------------------------------------

  const addAttachment = () => {
    setAttachments((prev) => [
      ...prev,
      { _key: nextKey(), id: null, document_type: 'air_ticket', original_filename: '',
        description: '', leg: '', traveller: '' },
    ]);
  };

  const updateAttachment = (key, field, value) => {
    setAttachments((prev) =>
      prev.map((a) => (a._key === key ? { ...a, [field]: value } : a))
    );
  };

  const removeAttachment = (key) => {
    setAttachments((prev) => prev.filter((a) => a._key !== key));
  };

  // ---- validation mirror ---------------------------------------------

  /**
   * Mirrors the server rules so the user is told before a round trip.
   * The server checks all of this again and is authoritative.
   */
  const clientErrors = () => {
    const errors = {};
    const activeLegs = legs;

    if (!formData.customer_id && !formData.customer_name) {
      errors.customer_name = 'A direct request needs a customer name.';
    }
    if (formData.journey_type === 'round_trip' && !formData.round_trip_mode) {
      errors.round_trip_mode = 'State whether the vehicle waits or the return is on another day.';
    }
    if (
      formData.journey_type === 'round_trip' &&
      formData.round_trip_mode === 'continuous_with_wait' &&
      formData.expected_waiting_hours === ''
    ) {
      errors.expected_waiting_hours = 'Expected waiting hours are required when the vehicle waits.';
    }
    if (formData.expected_waiting_hours !== '' && Number(formData.expected_waiting_hours) < 0) {
      errors.expected_waiting_hours = 'Waiting hours cannot be negative.';
    }

    const named = travellers.filter((t) => (t.full_name || '').trim()).length;
    if (named > Number(formData.declared_traveller_count)) {
      errors.declared_traveller_count =
        `${named} travellers are named but the declared count is ` +
        `${formData.declared_traveller_count}.`;
    }

    if (activeLegs.length === 0) {
      errors.legs = 'A request needs at least one service leg.';
    }
    activeLegs.forEach((leg) => {
      if (!leg.requested_pickup_datetime || !leg.pickup_location || !leg.dropoff_location) {
        errors.legs = 'Every leg needs a pickup, a drop-off and a requested vehicle pickup time.';
      }
      if (Number(leg.expected_passenger_count) < 1) {
        errors.legs = 'Every leg must expect at least one traveller.';
      }
    });

    if (formData.journey_type === 'round_trip') {
      const outbound = activeLegs.filter((l) => l.leg_role === 'outbound');
      const returns = activeLegs.filter((l) => l.leg_role === 'return');
      if (!outbound.length || !returns.length) {
        errors.legs = 'A round trip needs both an outbound and a return leg.';
      } else {
        const latestOut = Math.max(
          ...outbound.map((l) => new Date(l.requested_pickup_datetime).getTime())
        );
        const earliestBack = Math.min(
          ...returns.map((l) => new Date(l.requested_pickup_datetime).getTime())
        );
        if (earliestBack <= latestOut) {
          errors.legs = 'The return movement must be later than the outbound movement.';
        }
      }
    }

    // Capacity. The assignment stage picks a vehicle from these numbers, so a
    // request that contradicts itself - more passengers than seats, or fewer
    // expected on a leg than are actually assigned to it - would hand the next
    // stage an impossible requirement.
    const declared = Number(formData.declared_traveller_count) || 0;
    const maxLegPassengers = activeLegs.reduce(
      (peak, leg) => Math.max(peak, Number(leg.expected_passenger_count) || 0),
      0
    );
    if (formData.minimum_seats !== '' && Number(formData.minimum_seats) < maxLegPassengers) {
      errors.minimum_seats =
        `Minimum seats (${formData.minimum_seats}) is below the ${maxLegPassengers} ` +
        `passengers expected on the busiest leg.`;
    }
    if (maxLegPassengers > declared && declared > 0) {
      errors.capacity =
        `A leg expects ${maxLegPassengers} passengers but only ${declared} ` +
        `travellers are declared. Raise the declared count or lower the leg.`;
    }
    activeLegs.forEach((leg) => {
      const assigned = (leg.traveller_ids || []).length;
      if (assigned > Number(leg.expected_passenger_count || 0)) {
        errors.capacity =
          `${assigned} travellers are assigned to the ${leg.leg_role} leg but it ` +
          `expects ${leg.expected_passenger_count}.`;
      }
      leg.load_items.forEach((item) => {
        if (Number(item.quantity) < 0 || Number(item.estimated_weight_kg || 0) < 0) {
          errors.luggage = 'Luggage quantities and weights cannot be negative.';
        }
      });
    });

    Object.entries(fieldRequirements).forEach(([key, rule]) => {
      if (rule.state === 'required' && !corporateData[key]) {
        errors[key] = `${CORPORATE_FIELD_LABELS[key] || key} is required for this customer.`;
      }
    });

    return errors;
  };

  // ---- submit ---------------------------------------------------------

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return; // guards against a double click submitting twice

    const errors = clientErrors();
    setFieldErrors(errors);
    setSubmitAttempted(true);
    if (Object.keys(errors).length > 0) {
      // Jump to the first step that owns a failure. Refusing to submit while
      // the offending field sits on a step the user cannot see is the failure
      // mode a stepper introduces, so the form has to go there for them.
      const failingKeys = Object.keys(errors).map((field) => ERROR_STEP[field] || 'commercial');
      const target = steps.findIndex((step) => failingKeys.includes(step.key));
      if (target >= 0) setActiveStep(target);
      setError('Please correct the highlighted fields.');
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      const payload = {
        journey_type: formData.journey_type,
        declared_traveller_count: Number(formData.declared_traveller_count) || 1,
        accessibility_required: formData.accessibility_required,
        child_seat_required: formData.child_seat_required,
        priority: formData.priority,
        notes: formData.notes || null,
        internal_notes: formData.internal_notes || null,
        customer_reference: formData.customer_reference || null,
        requester_name: formData.requester_name || null,
        requester_phone: formData.requester_phone || null,
        requester_email: formData.requester_email || null,
        primary_contact_name: formData.primary_contact_name || null,
        primary_contact_phone: formData.primary_contact_phone || null,
        preferred_vehicle_type: formData.preferred_vehicle_type || null,
        minimum_seats: formData.minimum_seats === '' ? null : Number(formData.minimum_seats),
        travellers: travellers.map((t, index) => ({
          id: t.id || undefined,
          sequence: index + 1,
          full_name: (t.full_name || '').trim() || null,
          is_primary: !!t.is_primary,
          traveller_category: t.traveller_category,
          traveller_relationship: t.traveller_relationship,
          contact_number: t.contact_number || null,
          employee_reference: t.employee_reference || null,
          assistance_requirements: t.assistance_requirements || null,
        })),
        legs: legs.map((leg, index) => ({
          id: leg.id || undefined,
          sequence: index + 1,
          leg_role: leg.leg_role,
          service_type: leg.service_type,
          requested_pickup_datetime: toIso(leg.requested_pickup_datetime),
          pickup_location: leg.pickup_location,
          dropoff_location: leg.dropoff_location,
          expected_passenger_count: Number(leg.expected_passenger_count) || 1,
          accessibility_required: !!leg.accessibility_required,
          child_seat_required: !!leg.child_seat_required,
          special_instructions: leg.special_instructions || null,
          airline: leg.airline || null,
          flight_number: leg.flight_number || null,
          departure_airport: leg.departure_airport || null,
          arrival_airport: leg.arrival_airport || null,
          terminal: leg.terminal || null,
          scheduled_flight_departure: toIso(leg.scheduled_flight_departure),
          scheduled_flight_arrival: toIso(leg.scheduled_flight_arrival),
          ticket_baggage_allowance_kg:
            leg.ticket_baggage_allowance_kg === '' ? null : leg.ticket_baggage_allowance_kg,
          flight_notes: leg.flight_notes || null,
          traveller_ids: leg.traveller_ids || [],
          load_items: leg.load_items.map((item) => ({
            id: item.id || undefined,
            item_category: item.item_category,
            quantity: Number(item.quantity) || 0,
            estimated_weight_kg:
              item.estimated_weight_kg === '' ? null : item.estimated_weight_kg,
            handling_requirements: item.handling_requirements || null,
          })),
        })),
        attachments: attachments.map((a) => ({
          id: a.id || undefined,
          document_type: a.document_type,
          original_filename: a.original_filename,
          description: a.description || null,
          leg: a.leg || null,
          traveller: a.traveller || null,
        })),
      };

      if (formData.journey_type === 'round_trip') {
        payload.round_trip_mode = formData.round_trip_mode;
        payload.expected_waiting_hours =
          formData.round_trip_mode === 'continuous_with_wait' &&
          formData.expected_waiting_hours !== ''
            ? formData.expected_waiting_hours
            : null;
      }

      if (formData.customer_id) {
        payload.customer_id = formData.customer_id;
        payload.customer_type = 'corporate';
      } else {
        payload.customer_name = formData.customer_name;
        payload.customer_email = formData.customer_email || null;
        payload.customer_phone = formData.customer_phone || null;
      }

      // Only fields the policy actually exposes are sent. A hidden field is
      // rejected by the server, so sending one the user never saw would fail
      // the save for a value they did not enter.
      Object.entries(fieldRequirements).forEach(([key, rule]) => {
        if (['optional', 'required'].includes(rule.state) && corporateData[key]) {
          payload[key] = corporateData[key];
        }
      });

      const response = isEdit
        ? await apiClient.updateTripRequest(id, payload)
        : await apiClient.createTripRequest(payload);

      setWarnings(response.warnings || []);
      // The draft has served its purpose; leaving it would offer to restore
      // stale input the next time a request is created.
      try {
        window.localStorage.removeItem(draftKey(id));
      } catch {
        /* nothing to clean up */
      }
      setSuccessMessage(`Trip request ${isEdit ? 'updated' : 'created'} successfully.`);
      setTimeout(() => navigate(`/admin/trip-requests/${response.id || id}`), 1200);
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} trip request`);
    } finally {
      setSaving(false);
    }
  };

  // ---- draft autosave --------------------------------------------------
  //
  // Kept in localStorage rather than on the server. A server-side draft would
  // mean writing a TripRequest before the user has committed to one, which is
  // exactly what this stage is not supposed to do - and it would put
  // half-formed rows in front of operations. localStorage survives a reload,
  // a stray Back, and a closed tab, which covers what is actually lost.

  const draftPayload = useMemo(
    () => ({ formData, corporateData, travellers, legs, attachments, activeStep }),
    [formData, corporateData, travellers, legs, attachments, activeStep]
  );

  useEffect(() => {
    if (!hydrated.current || loading) return;
    const timer = setTimeout(() => {
      try {
        window.localStorage.setItem(
          draftKey(id),
          JSON.stringify({ savedAt: new Date().toISOString(), ...draftPayload })
        );
        setDraftSavedAt(new Date());
      } catch {
        // Quota or a private-browsing block. A lost draft is not worth
        // interrupting the user over, and nothing else depends on it.
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [draftPayload, id, loading]);

  const restoreDraft = useCallback(() => {
    try {
      const raw = window.localStorage.getItem(draftKey(id));
      if (!raw) return false;
      const draft = JSON.parse(raw);
      reserveKeys(draft.travellers);
      reserveKeys(draft.legs);
      reserveKeys(draft.attachments);
      if (draft.formData) setFormData(draft.formData);
      if (draft.corporateData) setCorporateData(draft.corporateData);
      if (draft.travellers?.length) setTravellers(draft.travellers);
      if (draft.legs?.length) setLegs(draft.legs);
      if (draft.attachments) setAttachments(draft.attachments);
      if (typeof draft.activeStep === 'number') setActiveStep(draft.activeStep);
      return true;
    } catch {
      return false;
    }
  }, [id]);

  const discardDraft = useCallback(() => {
    try {
      window.localStorage.removeItem(draftKey(id));
    } catch {
      /* nothing to clean up */
    }
  }, [id]);

  // Offer a draft only for a new request. Restoring one over a loaded record
  // would silently replace saved data with whatever was typed last.
  useEffect(() => {
    if (isEdit || hydrated.current) return;
    try {
      if (window.localStorage.getItem(draftKey(id))) setDraftRestored(true);
    } catch {
      /* storage unavailable */
    }
    hydrated.current = true;
  }, [id, isEdit]);

  useEffect(() => {
    if (isEdit && !loading) hydrated.current = true;
  }, [isEdit, loading]);

  // ---- step navigation -------------------------------------------------

  // Dropping the Commercial step when a corporate customer is cleared can
  // leave activeStep past the end of the list.
  const stepIndex = Math.min(activeStep, steps.length - 1);
  const currentStep = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;

  const goToStep = (index) => {
    setVisitedSteps((seen) => new Set(seen).add(currentStep.key));
    setActiveStep(Math.max(0, Math.min(index, steps.length - 1)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Live validation, so the stepper reflects the form as it stands rather than
  // as it stood at the last submit.
  const liveErrors = clientErrors();

  const stepErrors = (stepKey) =>
    Object.keys(liveErrors).filter((field) =>
      // Anything without an explicit mapping is a policy-driven corporate field.
      ERROR_STEP[field] ? ERROR_STEP[field] === stepKey : stepKey === 'commercial'
    );

  const stepHasError = (stepKey) =>
    (submitAttempted || visitedSteps.has(stepKey)) && stepErrors(stepKey).length > 0;

  // ---- render ----------------------------------------------------------

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const visibleCorporateKeys = Object.entries(fieldRequirements)
    .filter(([, rule]) => ['optional', 'required', 'readonly', 'defaulted'].includes(rule.state))
    .map(([key]) => key);

  const namedCount = travellers.filter((t) => (t.full_name || '').trim()).length;
  const pendingCount = Math.max(0, Number(formData.declared_traveller_count) - namedCount);
  const busiestLegPassengers = legs.reduce(
    (peak, leg) => Math.max(peak, Number(leg.expected_passenger_count) || 0),
    0
  );

  // Everything still blocking a submit, grouped by the step that owns it, for
  // the Review step to list.
  const outstanding = steps
    .map((step) => ({ step, messages: stepErrors(step.key).map((field) => liveErrors[field]) }))
    .filter((entry) => entry.messages.length > 0);

  const sectionTitle = (text, hint) => (
    <Grid size={{ xs: 12 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {text}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
      <Divider sx={{ mt: 1 }} />
    </Grid>
  );

  const effectiveProfile = profileChoice || requestProfile;

  const legHeading = (leg) =>
    leg.leg_role === 'outbound'
      ? 'Outbound'
      : leg.leg_role === 'return'
        ? 'Return'
        : `Additional leg ${leg.sequence}`;

  // ---- step panels -----------------------------------------------------

  const renderTypePanel = () => (
    <Grid container spacing={3}>
      {sectionTitle(
        'Who is this request for?',
        'This decides which details are collected and how the request is authorised.'
      )}

      {['direct', 'corporate'].map((profile) => {
        const selected = effectiveProfile === profile;
        return (
          <Grid size={{ xs: 12, md: 6 }} key={profile}>
            <Card
              variant="outlined"
              onClick={async () => {
                // Clearing the customer is what actually makes the request
                // direct; the handler warns first and returns false if the
                // user backs out, in which case the card must not move.
                if (profile === 'direct' && formData.customer_id) {
                  if (await handleCustomerChange({ target: { value: '' } })) {
                    setProfileChoice('direct');
                  }
                  return;
                }
                setProfileChoice(profile);
              }}
              sx={{
                cursor: 'pointer',
                height: '100%',
                borderColor: selected ? 'primary.main' : 'divider',
                borderWidth: selected ? 2 : 1,
                bgcolor: selected ? 'action.selected' : 'background.paper',
              }}
            >
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {profile === 'direct' ? 'Direct request' : 'Corporate request'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {profile === 'direct'
                    ? 'A walk-in or one-off customer. Name and contact details are captured on the request itself.'
                    : 'An account customer. Cost centre, approval and reference fields are applied from that customer’s policy.'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        );
      })}

      {effectiveProfile === 'corporate' && (
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth>
            <InputLabel>Corporate Customer</InputLabel>
            <Select
              name="customer_id"
              value={formData.customer_id}
              onChange={handleCustomerChange}
              label="Corporate Customer"
            >
              <MenuItem value="">None — direct customer</MenuItem>
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>
                  {customer.customer_name || customer.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {!formData.customer_id && (
            <Typography variant="caption" color="text.secondary">
              Select the account to load its billing policy.
            </Typography>
          )}
        </Grid>
      )}

      {sectionTitle('Journey shape')}

      <Grid size={{ xs: 12, md: 4 }}>
        <FormControl fullWidth>
          <InputLabel>Journey Type</InputLabel>
          <Select
            name="journey_type"
            value={formData.journey_type}
            onChange={handleJourneyTypeChange}
            label="Journey Type"
          >
            {JOURNEY_TYPES.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {formData.journey_type === 'round_trip' && (
        <Grid size={{ xs: 12, md: 4 }}>
          <FormControl fullWidth error={!!fieldErrors.round_trip_mode}>
            <InputLabel>Round Trip Mode</InputLabel>
            <Select
              name="round_trip_mode"
              value={formData.round_trip_mode}
              onChange={handleModeChange}
              label="Round Trip Mode"
            >
              {ROUND_TRIP_MODES.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}

      {/* Only a continuous round trip has a vehicle standing by, so this
          is the only case where waiting hours mean anything. */}
      {formData.journey_type === 'round_trip' &&
        formData.round_trip_mode === 'continuous_with_wait' && (
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              required
              type="number"
              name="expected_waiting_hours"
              label="Expected Waiting Hours"
              value={formData.expected_waiting_hours}
              onChange={handleChange}
              slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
              error={!!fieldErrors.expected_waiting_hours}
              helperText={
                fieldErrors.expected_waiting_hours ||
                'Captured as a requirement. No rate is applied at this stage.'
              }
            />
          </Grid>
        )}

      <Grid size={{ xs: 12, md: 4 }}>
        <FormControl fullWidth>
          <InputLabel>Priority</InputLabel>
          <Select name="priority" value={formData.priority} onChange={handleChange} label="Priority">
            <MenuItem value="normal">Normal</MenuItem>
            <MenuItem value="high">High</MenuItem>
            <MenuItem value="urgent">Urgent</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const renderCustomerPanel = () => (
    <Grid container spacing={3}>
      {sectionTitle(
        'Customer',
        effectiveProfile === 'corporate'
          ? 'Selected on the previous step. Change it there if it is wrong.'
          : 'Recorded on this request only — no customer account is created.'
      )}

      {formData.customer_id ? (
        <Grid size={{ xs: 12 }}>
          <Alert severity="info">
            Billing to{' '}
            <strong>
              {customers.find((c) => c.id === formData.customer_id)?.customer_name ||
                customers.find((c) => c.id === formData.customer_id)?.name ||
                'the selected account'}
            </strong>
            . Allocation and authorisation fields are on the Commercial &amp; Billing step.
          </Alert>
        </Grid>
      ) : (
        <>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              required
              name="customer_name"
              label="Customer Name"
              value={formData.customer_name}
              onChange={handleChange}
              error={!!fieldErrors.customer_name}
              helperText={fieldErrors.customer_name}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              name="customer_phone"
              label="Customer Phone"
              value={formData.customer_phone}
              onChange={handleChange}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              name="customer_email"
              label="Customer Email"
              type="email"
              value={formData.customer_email}
              onChange={handleChange}
            />
          </Grid>
        </>
      )}

      {sectionTitle(
        'Requester and contacts',
        'The requester need not travel, and the operational contact need not be the requester.'
      )}

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          name="requester_name"
          label="Requester / Booker"
          value={formData.requester_name}
          onChange={handleChange}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          name="requester_phone"
          label="Requester Phone"
          value={formData.requester_phone}
          onChange={handleChange}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          name="requester_email"
          label="Requester Email"
          type="email"
          value={formData.requester_email}
          onChange={handleChange}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          name="primary_contact_name"
          label="Primary Operational Contact"
          value={formData.primary_contact_name}
          onChange={handleChange}
          helperText="Who dispatch calls on the day"
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          name="primary_contact_phone"
          label="Primary Contact Phone"
          value={formData.primary_contact_phone}
          onChange={handleChange}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          name="customer_reference"
          label="Customer Reference"
          value={formData.customer_reference}
          onChange={handleChange}
          helperText="The customer’s own reference for this request"
        />
      </Grid>
    </Grid>
  );

  const renderJourneyPanel = () => (
    <Grid container spacing={3}>
      {sectionTitle(
        'Service legs',
        'Travellers sharing a pickup, drop-off and time belong on one leg, not one leg each.'
      )}

      {fieldErrors.legs && (
        <Grid size={{ xs: 12 }}>
          <Alert severity="error">{fieldErrors.legs}</Alert>
        </Grid>
      )}

      {legs.map((leg) => (
        <Grid size={{ xs: 12 }} key={leg._key}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1">{legHeading(leg)}</Typography>
              {leg.leg_role === 'additional' && (
                <IconButton aria-label="Remove leg" onClick={() => removeLeg(leg._key)}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Service Type"
                  value={leg.service_type}
                  onChange={(e) => updateLeg(leg._key, 'service_type', e.target.value)}
                >
                  {LEG_SERVICE_TYPES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  type="datetime-local"
                  label="Requested Vehicle Pickup"
                  value={leg.requested_pickup_datetime}
                  onChange={(e) =>
                    updateLeg(leg._key, 'requested_pickup_datetime', e.target.value)
                  }
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Expected Passengers"
                  value={leg.expected_passenger_count}
                  onChange={(e) =>
                    updateLeg(leg._key, 'expected_passenger_count', e.target.value)
                  }
                  slotProps={{ htmlInput: { min: 1 } }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Pickup Location"
                  value={leg.pickup_location}
                  onChange={(e) => updateLeg(leg._key, 'pickup_location', e.target.value)}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  required
                  size="small"
                  label="Drop-off Location"
                  value={leg.dropoff_location}
                  onChange={(e) => updateLeg(leg._key, 'dropoff_location', e.target.value)}
                />
              </Grid>

              {/* Flight details apply only to airport services. */}
              {AIRPORT_SERVICE_TYPES.includes(leg.service_type) && (
                <>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Flight Information — kept separate from the vehicle pickup time above
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Airline"
                      value={leg.airline || ''}
                      onChange={(e) => updateLeg(leg._key, 'airline', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Flight Number"
                      value={leg.flight_number || ''}
                      onChange={(e) => updateLeg(leg._key, 'flight_number', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Departure Airport"
                      value={leg.departure_airport || ''}
                      onChange={(e) => updateLeg(leg._key, 'departure_airport', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, md: 3 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Arrival Airport"
                      value={leg.arrival_airport || ''}
                      onChange={(e) => updateLeg(leg._key, 'arrival_airport', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="datetime-local"
                      label="Scheduled Flight Departure"
                      value={leg.scheduled_flight_departure || ''}
                      onChange={(e) =>
                        updateLeg(leg._key, 'scheduled_flight_departure', e.target.value)
                      }
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      type="datetime-local"
                      label="Scheduled Flight Arrival"
                      value={leg.scheduled_flight_arrival || ''}
                      onChange={(e) =>
                        updateLeg(leg._key, 'scheduled_flight_arrival', e.target.value)
                      }
                      slotProps={{ inputLabel: { shrink: true } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Tooltip title="What the ticket permits — not what will actually be carried">
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="Ticket Baggage Allowance (kg)"
                        value={leg.ticket_baggage_allowance_kg}
                        onChange={(e) =>
                          updateLeg(leg._key, 'ticket_baggage_allowance_kg', e.target.value)
                        }
                        slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
                      />
                    </Tooltip>
                  </Grid>
                </>
              )}

              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Special Instructions"
                  value={leg.special_instructions || ''}
                  onChange={(e) => updateLeg(leg._key, 'special_instructions', e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      ))}

      <Grid size={{ xs: 12 }}>
        <Button startIcon={<AddIcon />} onClick={addLeg}>
          Add Service Leg
        </Button>
      </Grid>
    </Grid>
  );

  const renderTravellersPanel = () => (
    <Grid container spacing={3}>
      {sectionTitle(
        'Travellers',
        'Leave a name blank to hold a place for someone not yet known.'
      )}

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          type="number"
          name="declared_traveller_count"
          label="Declared Traveller Count"
          value={formData.declared_traveller_count}
          onChange={handleChange}
          slotProps={{ htmlInput: { min: 1 } }}
          error={!!fieldErrors.declared_traveller_count}
          helperText={
            fieldErrors.declared_traveller_count || `${namedCount} named, ${pendingCount} pending`
          }
        />
      </Grid>

      {fieldErrors.capacity && (
        <Grid size={{ xs: 12 }}>
          <Alert severity="error">{fieldErrors.capacity}</Alert>
        </Grid>
      )}
      {fieldErrors.luggage && (
        <Grid size={{ xs: 12 }}>
          <Alert severity="error">{fieldErrors.luggage}</Alert>
        </Grid>
      )}

      {travellers.map((traveller) => (
        <Grid size={{ xs: 12 }} key={traveller._key}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid size={{ xs: 12, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Full Name"
                  value={traveller.full_name || ''}
                  onChange={(e) => updateTraveller(traveller._key, 'full_name', e.target.value)}
                  placeholder="Not yet known"
                />
              </Grid>
              <Grid size={{ xs: 6, md: 2 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Category"
                  value={traveller.traveller_category}
                  onChange={(e) =>
                    updateTraveller(traveller._key, 'traveller_category', e.target.value)
                  }
                >
                  {TRAVELLER_CATEGORIES.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, md: 2 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Relationship"
                  value={traveller.traveller_relationship}
                  onChange={(e) =>
                    updateTraveller(traveller._key, 'traveller_relationship', e.target.value)
                  }
                >
                  {TRAVELLER_RELATIONSHIPS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 6, md: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Contact Number"
                  value={traveller.contact_number || ''}
                  onChange={(e) =>
                    updateTraveller(traveller._key, 'contact_number', e.target.value)
                  }
                />
              </Grid>
              {effectiveProfile === 'corporate' && (
                <Grid size={{ xs: 6, md: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Employee Reference"
                    value={traveller.employee_reference || ''}
                    onChange={(e) =>
                      updateTraveller(traveller._key, 'employee_reference', e.target.value)
                    }
                  />
                </Grid>
              )}
              <Grid size={{ xs: 6, md: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!traveller.is_primary}
                      onChange={(e) =>
                        updateTraveller(traveller._key, 'is_primary', e.target.checked)
                      }
                    />
                  }
                  label="Primary"
                />
              </Grid>
              <Grid size={{ xs: 6, md: 1 }}>
                <IconButton
                  aria-label="Remove traveller"
                  onClick={() => removeTraveller(traveller._key)}
                  disabled={travellers.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      ))}

      <Grid size={{ xs: 12 }}>
        <Button startIcon={<AddIcon />} onClick={addTraveller}>
          Add Traveller
        </Button>
      </Grid>

      {sectionTitle(
        'Per-leg participation and luggage',
        'Who is on each leg, and what is actually carried on it — not what the ticket allows.'
      )}

      {legs.map((leg) => (
        <Grid size={{ xs: 12 }} key={leg._key}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {legHeading(leg)}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {leg.pickup_location || '—'} → {leg.dropoff_location || '—'} · expects{' '}
                {leg.expected_passenger_count} passenger
                {Number(leg.expected_passenger_count) === 1 ? '' : 's'}
              </Typography>
            </Typography>

            {/* Traveller participation, which may differ per leg. Only saved
                travellers have an id to link against. */}
            {travellers.some((t) => t.id) ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Travellers on this leg
                </Typography>
                {travellers
                  .filter((t) => t.id)
                  .map((t) => (
                    <FormControlLabel
                      key={t.id}
                      control={
                        <Checkbox
                          size="small"
                          checked={(leg.traveller_ids || []).includes(t.id)}
                          onChange={() => toggleLegTraveller(leg._key, t.id)}
                        />
                      }
                      label={t.full_name || `Traveller ${t.sequence}`}
                    />
                  ))}
                <Button size="small" onClick={() => applyAllTravellersToLeg(leg._key)}>
                  Apply all travellers
                </Button>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Travellers can be assigned to individual legs once the request has been saved.
              </Typography>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Expected Actual Luggage
              </Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => addLoadItem(leg._key)}>
                Add item
              </Button>
              {leg.leg_role === 'return' && (
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copyOutboundLuggage(leg._key)}
                >
                  Copy from outbound
                </Button>
              )}
            </Box>

            {leg.load_items.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No luggage recorded for this leg.
              </Typography>
            )}

            {leg.load_items.map((item) => (
              <Grid container spacing={1} key={item._key} sx={{ mb: 1 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Item"
                    value={item.item_category}
                    onChange={(e) =>
                      updateLoadItem(leg._key, item._key, 'item_category', e.target.value)
                    }
                  >
                    {LOAD_CATEGORIES.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Quantity"
                    value={item.quantity}
                    onChange={(e) =>
                      updateLoadItem(leg._key, item._key, 'quantity', e.target.value)
                    }
                    slotProps={{ htmlInput: { min: 0 } }}
                  />
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label="Weight (kg)"
                    value={item.estimated_weight_kg || ''}
                    onChange={(e) =>
                      updateLoadItem(leg._key, item._key, 'estimated_weight_kg', e.target.value)
                    }
                    slotProps={{ htmlInput: { min: 0, step: 0.5 } }}
                  />
                </Grid>
                <Grid size={{ xs: 10, md: 3 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Handling"
                    value={item.handling_requirements || ''}
                    onChange={(e) =>
                      updateLoadItem(leg._key, item._key, 'handling_requirements', e.target.value)
                    }
                  />
                </Grid>
                <Grid size={{ xs: 2, md: 1 }}>
                  <IconButton
                    aria-label="Remove load item"
                    onClick={() => removeLoadItem(leg._key, item._key)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))}
          </Paper>
        </Grid>
      ))}
    </Grid>
  );

  const renderVehiclePanel = () => (
    <Grid container spacing={3}>
      {sectionTitle(
        'Vehicle requirements',
        'Captured as a requirement for the assignment stage. No vehicle is reserved here.'
      )}

      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          type="number"
          name="minimum_seats"
          label="Minimum Seats"
          value={formData.minimum_seats}
          onChange={handleChange}
          slotProps={{ htmlInput: { min: 1 } }}
          error={!!fieldErrors.minimum_seats}
          helperText={
            fieldErrors.minimum_seats ||
            `Busiest leg expects ${busiestLegPassengers} passenger${
              busiestLegPassengers === 1 ? '' : 's'
            }`
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <TextField
          fullWidth
          name="preferred_vehicle_type"
          label="Requested Vehicle Category"
          value={formData.preferred_vehicle_type}
          onChange={handleChange}
          helperText="A preference, not a reservation"
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <FormControlLabel
          control={
            <Checkbox
              name="accessibility_required"
              checked={formData.accessibility_required}
              onChange={handleChange}
            />
          }
          label="Accessibility assistance required"
        />
        <FormControlLabel
          control={
            <Checkbox
              name="child_seat_required"
              checked={formData.child_seat_required}
              onChange={handleChange}
            />
          }
          label="Child seat required"
        />
      </Grid>

      {fieldErrors.capacity && (
        <Grid size={{ xs: 12 }}>
          <Alert severity="error">{fieldErrors.capacity}</Alert>
        </Grid>
      )}
    </Grid>
  );

  const renderCommercialPanel = () => (
    <Grid container spacing={3}>
      {sectionTitle(
        'Corporate allocation and authorisation',
        'Which of these are required is configured per customer and enforced on the server.'
      )}

      {visibleCorporateKeys.length === 0 ? (
        <Grid size={{ xs: 12 }}>
          <Alert severity="info">
            This customer’s policy exposes no allocation fields. Nothing is needed here.
          </Alert>
        </Grid>
      ) : (
        visibleCorporateKeys.map((key) => {
          const rule = fieldRequirements[key] || {};
          const locked = ['readonly', 'defaulted'].includes(rule.state);
          return (
            <Grid size={{ xs: 12, md: 4 }} key={key}>
              <TextField
                fullWidth
                size="small"
                name={key}
                label={CORPORATE_FIELD_LABELS[key] || key}
                value={locked ? rule.default_value || '' : corporateData[key] || ''}
                onChange={handleCorporateChange}
                required={rule.state === 'required'}
                disabled={locked}
                error={!!fieldErrors[key]}
                helperText={fieldErrors[key] || (locked ? 'Set by policy' : '')}
              />
            </Grid>
          );
        })
      )}
    </Grid>
  );

  const renderReviewPanel = () => (
    <Grid container spacing={3}>
      {sectionTitle(
        'Attachments',
        'Supporting evidence only. The itinerary is still required — a ticket cannot stand in for it.'
      )}

      <Grid size={{ xs: 12 }}>
        <Alert severity="info" sx={{ mb: 1 }}>
          This build records attachment details but does not yet store files. File upload and secure
          download are pending a reviewed document-storage subsystem.
        </Alert>
      </Grid>

      {attachments.map((attachment) => (
        <Grid size={{ xs: 12 }} key={attachment._key}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                fullWidth
                size="small"
                label="Document Type"
                value={attachment.document_type}
                onChange={(e) =>
                  updateAttachment(attachment._key, 'document_type', e.target.value)
                }
              >
                {ATTACHMENT_TYPES.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Filename"
                value={attachment.original_filename}
                onChange={(e) =>
                  updateAttachment(attachment._key, 'original_filename', e.target.value)
                }
              />
            </Grid>
            <Grid size={{ xs: 10, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={attachment.description || ''}
                onChange={(e) => updateAttachment(attachment._key, 'description', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 2, md: 1 }}>
              <IconButton
                aria-label="Remove attachment"
                onClick={() => removeAttachment(attachment._key)}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      ))}

      <Grid size={{ xs: 12 }}>
        <Button startIcon={<AddIcon />} onClick={addAttachment}>
          Add Attachment
        </Button>
      </Grid>

      {sectionTitle('Notes')}

      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          multiline
          rows={3}
          name="notes"
          label="Notes"
          value={formData.notes}
          onChange={handleChange}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <TextField
          fullWidth
          multiline
          rows={3}
          name="internal_notes"
          label="Internal Notes"
          value={formData.internal_notes}
          onChange={handleChange}
          helperText="Not shown to portal users."
        />
      </Grid>

      {sectionTitle('Summary')}

      <Grid size={{ xs: 12 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2">
            <strong>{effectiveProfile === 'corporate' ? 'Corporate' : 'Direct'}</strong>{' '}
            {formData.journey_type === 'round_trip' ? 'round trip' : 'one-way'} request
            {formData.journey_type === 'round_trip' && formData.round_trip_mode
              ? ` (${ROUND_TRIP_MODES.find((m) => m.value === formData.round_trip_mode)?.label})`
              : ''}
            {' — '}
            {formData.declared_traveller_count} traveller
            {Number(formData.declared_traveller_count) === 1 ? '' : 's'} ({namedCount} named,{' '}
            {pendingCount} pending), {legs.length} leg{legs.length === 1 ? '' : 's'},{' '}
            {attachments.length} attachment{attachments.length === 1 ? '' : 's'}.
          </Typography>
          {legs.map((leg) => (
            <Typography key={leg._key} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {leg.leg_role}: {leg.pickup_location || '—'} → {leg.dropoff_location || '—'}
              {leg.requested_pickup_datetime
                ? ` @ ${new Date(leg.requested_pickup_datetime).toLocaleString()}`
                : ''}
              {leg.load_items.length ? ` · ${leg.load_items.length} load item(s)` : ''}
            </Typography>
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Saving records the request only. No vehicle, driver, dispatch or invoice is created.
          </Typography>
        </Paper>
      </Grid>

      {/* Everything still outstanding, gathered in one place. Without this the
          only way to find a blocking field would be to open every step. */}
      {outstanding.length > 0 && (
        <Grid size={{ xs: 12 }}>
          <Alert severity="warning">
            <AlertTitle>Not ready to submit</AlertTitle>
            {outstanding.map(({ step, messages }) => (
              <Box key={step.key} sx={{ mb: 0.5 }}>
                <Button
                  size="small"
                  onClick={() => goToStep(steps.findIndex((s) => s.key === step.key))}
                >
                  {step.label}
                </Button>
                <Typography variant="body2" component="span">
                  {messages.join(' ')}
                </Typography>
              </Box>
            ))}
          </Alert>
        </Grid>
      )}
    </Grid>
  );

  const panelRenderers = {
    type: renderTypePanel,
    customer: renderCustomerPanel,
    journey: renderJourneyPanel,
    travellers: renderTravellersPanel,
    vehicle: renderVehiclePanel,
    commercial: renderCommercialPanel,
    review: renderReviewPanel,
  };

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: 3,
          gap: 2,
        }}
      >
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/trip-requests')}
            sx={{ mb: 1 }}
          >
            Back to Trip Requests
          </Button>
          <Typography variant="h4" component="h1">
            {isEdit ? 'Edit Trip Request' : 'New Trip Request'}
          </Typography>
          <Chip
            size="small"
            sx={{ mt: 1 }}
            color={effectiveProfile === 'corporate' ? 'primary' : 'default'}
            label={effectiveProfile === 'corporate' ? 'Corporate request' : 'Direct request'}
          />
        </Box>
        {draftSavedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ pt: 1 }}>
            Draft saved {draftSavedAt.toLocaleTimeString()}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Please check these times</AlertTitle>
          {warnings.map((warning) => (
            <Typography key={`${warning.code}-${warning.leg_sequence}`} variant="body2">
              Leg {warning.leg_sequence}: {warning.message}
            </Typography>
          ))}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Stepper
          nonLinear
          alternativeLabel
          activeStep={stepIndex}
          sx={{ mb: 3, flexWrap: 'wrap', rowGap: 2 }}
        >
          {steps.map((step, index) => {
            const failed = stepHasError(step.key);
            return (
              <Step key={step.key} completed={visitedSteps.has(step.key) && !failed}>
                {/* The caption goes on StepButton, not StepLabel: StepButton
                    clones its child with its own `optional`, so one set on the
                    label is discarded and nothing renders. */}
                <StepButton
                  onClick={() => goToStep(index)}
                  optional={
                    <Typography
                      variant="caption"
                      component="span"
                      color={failed ? 'error' : 'text.secondary'}
                    >
                      {failed ? `${stepErrors(step.key).length} to fix` : step.caption}
                    </Typography>
                  }
                >
                  <StepLabel error={failed}>{step.label}</StepLabel>
                </StepButton>
              </Step>
            );
          })}
        </Stepper>

        <form onSubmit={handleSubmit}>
          {panelRenderers[currentStep.key]()}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/admin/trip-requests')} disabled={saving}>
              Cancel
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Step {stepIndex + 1} of {steps.length}
            </Typography>
            <Button
              type="button"
              onClick={() => goToStep(stepIndex - 1)}
              disabled={stepIndex === 0 || saving}
            >
              Back
            </Button>
            {isLastStep ? (
              <Button
                type="submit"
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                disabled={saving}
              >
                {saving ? 'Saving…' : isEdit ? 'Update Trip Request' : 'Create Trip Request'}
              </Button>
            ) : (
              <Button type="button" variant="contained" onClick={() => goToStep(stepIndex + 1)}>
                Next
              </Button>
            )}
          </Box>
        </form>
      </Paper>

      <Snackbar
        open={draftRestored}
        message="An unsaved draft of this request was found."
        action={
          <>
            <Button
              size="small"
              color="primary"
              onClick={() => {
                restoreDraft();
                setDraftRestored(false);
              }}
            >
              Restore
            </Button>
            <Button
              size="small"
              color="inherit"
              onClick={() => {
                discardDraft();
                setDraftRestored(false);
              }}
            >
              Discard
            </Button>
          </>
        }
      />
    </Container>
  );
};

export default TripRequestForm;
